/**
 * Crawler Job Provider Component
 * Manages crawler jobs and provides context for external source display
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo,useState } from 'react';

import { OcChatConfig } from '../../../../types/oc';
import { getCrawlerJobs } from '../../services/ocEndpoints';
import { CrawlerJob } from '../../types/apiTypes'; 

interface CrawlerJobContextValue {
    crawlerJobs: CrawlerJob[];
    getCrawlerNameByKey: (crawlerJobKey: string) => string | undefined;
    getCrawlerNameByObjectType: (itemType: string) => string | undefined;
}

const CrawlerJobsContext = createContext<CrawlerJobContextValue | undefined>(undefined);
CrawlerJobsContext.displayName = 'CrawlerJobsContext';

export interface CrawlerJobsProviderProps {
    children: React.ReactNode;
    config?: OcChatConfig;
}

export const CrawlerJobsProvider: React.FC<CrawlerJobsProviderProps> = ({ children, config }) => {
    const [crawlerJobs, setCrawlerJobs] = useState<CrawlerJob[]>([]);

    useEffect(() => {
        if (!config) {
            setCrawlerJobs([]);
            return;
        }

        getCrawlerJobs(config)
            .then((jobs) => {
                setCrawlerJobs(jobs);
            })
            .catch(() => {
                // Fail silently - if crawler data is unavailable, sources will fall back to default display
                setCrawlerJobs([]);
            });
    }, [config]);

    const getCrawlerNameByKey = useCallback((crawlerJobKey: string): string | undefined => {
        const job = crawlerJobs.find((j) => j.CrawlerJobKey === crawlerJobKey);
        return job?.CrawlerName;
    }, [crawlerJobs]);

    const getCrawlerNameByObjectType = useCallback((itemType: string): string | undefined => {
        const crawlerMatch = itemType.toLowerCase().match(/^crawl_(.+)$/);
        const crawlerKey = crawlerMatch?.[1];
        return crawlerKey ? getCrawlerNameByKey(crawlerKey) : undefined;
    }, [getCrawlerNameByKey]);

    const value: CrawlerJobContextValue = useMemo(() => ({
        crawlerJobs,
        getCrawlerNameByKey,
        getCrawlerNameByObjectType
    }), [crawlerJobs, getCrawlerNameByKey, getCrawlerNameByObjectType]);

    return <CrawlerJobsContext.Provider value={value}>{children}</CrawlerJobsContext.Provider>;
};

export const useCrawlerJobs = (): CrawlerJobContextValue => {
    const context = useContext(CrawlerJobsContext);
    if (context === undefined) {
        throw new Error('useCrawlerJobs must be used within a CrawlerJobsProvider');
    }
    return context;
};